import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdAutoFixHigh, MdCancel, MdSave } from "react-icons/md";
import {
  CreateToolRequest,
  ToolConfig,
  toolService,
} from "services/toolService";

interface ToolCreatorProps {
  serverId: string;
  onSave: (newTool: ToolConfig) => void;
  onCancel: () => void;
}

const DESCRIPTION_MAX_SYMBOLS = 1000;
// The name is sent to the LLM as the function name — OpenAI rejects anything else with HTTP 400.
const TOOL_NAME_MAX_SYMBOLS = 64;
const TOOL_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;

const ToolCreator = ({ serverId, onSave, onCancel }: ToolCreatorProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [httpMethod, setHttpMethod] = useState("GET");
  const [endpointPath, setEndpointPath] = useState("");
  const [inputSchemaJson, setInputSchemaJson] = useState(
    '{\n  "type": "object",\n  "properties": {},\n  "required": []\n}',
  );
  const [outputSchemaJson, setOutputSchemaJson] = useState("");
  const [requestBodyTemplate, setRequestBodyTemplate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [schemaGenerationSuccess, setSchemaGenerationSuccess] = useState(false);
  const descriptionLength = description.length;
  const isDescriptionTooLong = descriptionLength > DESCRIPTION_MAX_SYMBOLS;

  /**
   * Infers JSON Schema from an example JSON object
   * Supports:
   * - Primitive types (string, number, integer, boolean)
   * - Template variables like {{variableName}}
   * - Arrays and nested objects
   * - Automatic required field detection
   */
  const inferSchemaFromJson = (
    jsonValue: any,
    isRequired: boolean = false,
  ): any => {
    if (jsonValue === null) {
      return { type: "null" };
    }

    const valueType = typeof jsonValue;

    if (valueType === "boolean") {
      return { type: "boolean", description: "" };
    }

    if (valueType === "number") {
      return {
        type: Number.isInteger(jsonValue) ? "integer" : "number",
        description: "",
      };
    }

    if (valueType === "string") {
      // Check if it's a template variable like {{variable}}
      if (jsonValue.match(/^{{.*}}$/)) {
        return {
          type: "string",
          description: `Parameter: ${jsonValue.replace(/[{}]/g, "")}`,
        };
      }
      return { type: "string", description: "" };
    }

    if (Array.isArray(jsonValue)) {
      if (jsonValue.length === 0) {
        return {
          type: "array",
          items: { type: "string" },
          description: "",
        };
      }
      // Infer schema from first item
      return {
        type: "array",
        items: inferSchemaFromJson(jsonValue[0], false),
        description: "",
      };
    }

    if (valueType === "object") {
      const properties: any = {};
      const required: string[] = [];

      for (const key in jsonValue) {
        properties[key] = inferSchemaFromJson(jsonValue[key], true);
        required.push(key);
      }

      return {
        type: "object",
        properties,
        required: required.length > 0 ? required : undefined,
        description: "",
      };
    }

    return { type: "string", description: "" };
  };

  const generateSchemaFromTemplate = () => {
    try {
      setSchemaGenerationSuccess(false);
      setValidationErrors({});

      if (!requestBodyTemplate.trim()) {
        setValidationErrors({
          requestBodyTemplate: t("app.servers.tools.enterJsonFirst"),
        });
        return;
      }

      const exampleJson = JSON.parse(requestBodyTemplate);
      const schema = inferSchemaFromJson(exampleJson, false);

      // Format the schema nicely
      const formattedSchema = JSON.stringify(schema, null, 2);
      setInputSchemaJson(formattedSchema);
      setSchemaGenerationSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSchemaGenerationSuccess(false), 3000);
    } catch (err) {
      setValidationErrors({
        requestBodyTemplate: t("app.servers.tools.invalidJsonForSchema"),
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    const trimmedName = name.trim();
    if (!trimmedName) {
      errors.name = t("app.servers.tools.nameRequired");
    } else if (trimmedName.length > TOOL_NAME_MAX_SYMBOLS) {
      errors.name = t("app.servers.tools.errors.nameTooLong", {
        max: TOOL_NAME_MAX_SYMBOLS,
      });
    } else if (!TOOL_NAME_PATTERN.test(trimmedName)) {
      errors.name = t("app.servers.tools.errors.nameInvalidChars");
    }

    if (!httpMethod) {
      errors.httpMethod = t("app.servers.tools.methodRequired");
    }

    if (isDescriptionTooLong) {
      errors.description = t("app.servers.tools.descriptionTooLong", {
        over: descriptionLength - DESCRIPTION_MAX_SYMBOLS,
        max: DESCRIPTION_MAX_SYMBOLS,
      });
    }

    if (!endpointPath.trim()) {
      errors.endpointPath = t("app.servers.tools.endpointRequired");
    } else if (!endpointPath.startsWith("/")) {
      errors.endpointPath = t("app.servers.tools.endpointMustStartWithSlash");
    }

    // Validate JSON schema if provided
    if (inputSchemaJson.trim()) {
      try {
        JSON.parse(inputSchemaJson);
      } catch {
        errors.inputSchemaJson = t("app.servers.tools.invalidInputSchema");
      }
    }

    if (outputSchemaJson.trim()) {
      try {
        JSON.parse(outputSchemaJson);
      } catch {
        errors.outputSchemaJson = t("app.servers.tools.invalidOutputSchema");
      }
    }

    if (requestBodyTemplate.trim()) {
      try {
        JSON.parse(requestBodyTemplate);
      } catch {
        errors.requestBodyTemplate = t("app.servers.tools.invalidRequestBody");
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const createRequest: CreateToolRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        httpMethod: httpMethod,
        endpointPath: endpointPath.trim(),
        inputSchemaJson: inputSchemaJson.trim() || undefined,
        outputSchemaJson: outputSchemaJson.trim() || undefined,
        requestBodyTemplate: requestBodyTemplate.trim() || undefined,
        isActive,
        displayName: name.trim(),
        riskLevel:
          httpMethod === "DELETE" || httpMethod === "PATCH"
            ? "high"
            : httpMethod === "POST" || httpMethod === "PUT"
              ? "medium"
              : "low",
        isDestructive: httpMethod === "DELETE",
        isBulk: false,
        requiresApproval: httpMethod !== "GET" && httpMethod !== "HEAD", // Require approval for non-read operations
      };

      const newTool = await toolService.createTool(serverId, createRequest);
      onSave(newTool);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.servers.tools.errors.createFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card.Root>
      <Card.Body>
        <Flex direction="column" mb={6}>
          <Heading
            size="lg"
            mb={2}
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.createTool")}
          </Heading>
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.servers.tools.createDescription")}
          </Text>
        </Flex>

        {error && (
          <Box mb={4} bg="red.100" p={3} borderRadius="lg">
            <Text fontSize="sm" color="red.700">
              {error}
            </Text>
          </Box>
        )}

        <Flex direction="column" gap={4}>
          {/* Tool Name */}
          <Flex direction="column">
            <Text
              mb={2}
              fontSize="sm"
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.servers.tools.toolName")}
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("app.servers.tools.toolNamePlaceholder")}
              maxLength={TOOL_NAME_MAX_SYMBOLS}
            />
            {validationErrors.name && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {validationErrors.name}
              </Text>
            )}
          </Flex>

          {/* Description */}
          <Flex direction="column">
            <Text
              mb={2}
              fontSize="sm"
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.servers.description")}
            </Text>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("app.servers.tools.descriptionPlaceholder")}
              rows={3}
            />
            <Text
              fontSize="xs"
              color={
                isDescriptionTooLong
                  ? "red.600"
                  : { base: "gray.600", _dark: "gray.400" }
              }
              mt={1}
            >
              {t("app.servers.tools.descriptionLength", {
                count: descriptionLength,
                max: DESCRIPTION_MAX_SYMBOLS,
              })}
            </Text>
            {isDescriptionTooLong && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {t("app.servers.tools.descriptionTooLong", {
                  over: descriptionLength - DESCRIPTION_MAX_SYMBOLS,
                  max: DESCRIPTION_MAX_SYMBOLS,
                })}
              </Text>
            )}
            {validationErrors.description && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {validationErrors.description}
              </Text>
            )}
          </Flex>

          {/* HTTP Method and Endpoint Path */}
          <Flex gap={4}>
            <Flex direction="column" flex={1}>
              <Text
                mb={2}
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.httpMethod")}
              </Text>
              <Select
                items={[
                  { label: t("app.servers.httpMethods.get"), value: "GET" },
                  { label: t("app.servers.httpMethods.post"), value: "POST" },
                  { label: t("app.servers.httpMethods.put"), value: "PUT" },
                  { label: t("app.servers.httpMethods.patch"), value: "PATCH" },
                  {
                    label: t("app.servers.httpMethods.delete"),
                    value: "DELETE",
                  },
                ]}
                value={[httpMethod]}
                onValueChange={(e: any) => setHttpMethod(e.value[0] || "GET")}
                placeholder={t("app.servers.tools.selectMethod")}
              />
              {validationErrors.httpMethod && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  {validationErrors.httpMethod}
                </Text>
              )}
            </Flex>

            <Flex direction="column" flex={2}>
              <Text
                mb={2}
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.endpointPath")}
              </Text>
              <Input
                value={endpointPath}
                onChange={(e) => setEndpointPath(e.target.value)}
                placeholder={t("app.servers.tools.endpointPlaceholder")}
              />
              {validationErrors.endpointPath && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  {validationErrors.endpointPath}
                </Text>
              )}
            </Flex>
          </Flex>

          {/* Request Body Template */}
          <Flex direction="column">
            <Flex justify="space-between" align="center" mb={2}>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.requestBodyTemplate")}
              </Text>
              <Badge
                bg={{ base: "blue.100", _dark: "blue.900" }}
                color={{ base: "blue.700", _dark: "blue.300" }}
                px={2}
                py={0.5}
                fontSize="xs"
              >
                {t("app.common.optional")}
              </Badge>
            </Flex>
            <Textarea
              value={requestBodyTemplate}
              onChange={(e) => setRequestBodyTemplate(e.target.value)}
              placeholder={t("app.servers.tools.requestBodyPlaceholder")}
              rows={6}
              fontFamily="monospace"
              fontSize="sm"
            />
            {validationErrors.requestBodyTemplate && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {validationErrors.requestBodyTemplate}
              </Text>
            )}
            <Text
              fontSize="xs"
              color={{ base: "gray.600", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.servers.tools.requestBodyHint")}
            </Text>
          </Flex>

          {/* Input Schema */}
          <Flex direction="column">
            <Flex justify="space-between" align="center" mb={2}>
              <Flex align="center" gap={2}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={{ base: "navy.700", _dark: "white" }}
                >
                  {t("app.servers.tools.inputSchema")}
                </Text>
                <Badge
                  bg={{ base: "blue.100", _dark: "blue.900" }}
                  color={{ base: "blue.700", _dark: "blue.300" }}
                  px={2}
                  py={0.5}
                  fontSize="xs"
                >
                  {t("app.common.optional")}
                </Badge>
              </Flex>
              <Button
                onClick={generateSchemaFromTemplate}
                variant="outline"
                colorPalette="purple"
                size="sm"
              >
                <MdAutoFixHigh />
                {t("app.servers.tools.generateFromTemplate")}
              </Button>
            </Flex>
            {schemaGenerationSuccess && (
              <Box mb={2} bg="green.100" p={2} borderRadius="md">
                <Text fontSize="xs" color="green.700">
                  {t("app.servers.tools.schemaGeneratedSuccess")}
                </Text>
              </Box>
            )}
            <Textarea
              value={inputSchemaJson}
              onChange={(e) => setInputSchemaJson(e.target.value)}
              placeholder={t("app.servers.tools.schemaPlaceholder")}
              rows={8}
              fontFamily="monospace"
              fontSize="sm"
            />
            {validationErrors.inputSchemaJson && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {validationErrors.inputSchemaJson}
              </Text>
            )}
            <Text
              fontSize="xs"
              color={{ base: "gray.600", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.servers.tools.inputSchemaHint")}
            </Text>
          </Flex>

          {/* Output Schema */}
          <Flex direction="column">
            <Flex justify="space-between" align="center" mb={2}>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.outputSchema")}
              </Text>
              <Badge
                bg={{ base: "blue.100", _dark: "blue.900" }}
                color={{ base: "blue.700", _dark: "blue.300" }}
                px={2}
                py={0.5}
                fontSize="xs"
              >
                {t("app.common.optional")}
              </Badge>
            </Flex>
            <Textarea
              value={outputSchemaJson}
              onChange={(e) => setOutputSchemaJson(e.target.value)}
              placeholder={t("app.servers.tools.schemaPlaceholder")}
              rows={6}
              fontFamily="monospace"
              fontSize="sm"
            />
            {validationErrors.outputSchemaJson && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {validationErrors.outputSchemaJson}
              </Text>
            )}
            <Text
              fontSize="xs"
              color={{ base: "gray.600", _dark: "gray.400" }}
              mt={1}
            >
              {t("app.servers.tools.outputSchemaHint")}
            </Text>
          </Flex>

          {/* Tool Status */}
          <Flex direction="column">
            <Flex justify="space-between" align="center" mb={2}>
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.initialStatus")}
              </Text>
              <Button
                onClick={() => setIsActive(!isActive)}
                variant="outline"
                colorPalette={isActive ? "green" : "gray"}
                size="sm"
              >
                {isActive ? t("app.common.active") : t("app.common.inactive")}
              </Button>
            </Flex>
            <Text fontSize="xs" color={{ base: "gray.600", _dark: "gray.400" }}>
              {isActive
                ? t("app.servers.tools.activeAfterCreation")
                : t("app.servers.tools.inactiveAfterCreation")}
            </Text>
          </Flex>
        </Flex>

        <HStack gap={3} mt={6}>
          <Button
            onClick={handleSave}
            disabled={saving || isDescriptionTooLong}
            variant="solid"
            colorPalette="blue"
            flex={1}
          >
            <MdSave />
            {saving
              ? t("app.servers.tools.creating")
              : t("app.servers.tools.createButton")}
          </Button>
          <Button
            onClick={onCancel}
            disabled={saving}
            variant="subtle"
            colorPalette="gray"
            flex={1}
          >
            <MdCancel />
            {t("app.common.cancel")}
          </Button>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

export default ToolCreator;
